'use client';

import NoGutterRow from '@/components/layout/NoGutterRow';
import { useCart } from '@/features/checkout/CartProvider';
import CouponCard from '@/features/checkout/components/CouponCard';
import SavedAmount from '@/features/checkout/components/SavedAmount.jsx';
import { formatCurrency } from '@/features/orders/orderPresentation';
import BuyBoxBell from '@/features/primitives/bell/BuyBoxBell';
import Link from 'next/link';
import { useMemo } from 'react';
import { Button, Col } from 'react-bootstrap';
import { FaTimes } from 'react-icons/fa';
import { interpolateCartMessage, readCartMessage } from './cartMessages';
import { getBenefitLabel } from './cartUtils';

const benefitIcons = {
  free_shipping: '🚀',
  percentage: '💎',
  fixed: '⚖️',
  buy_x_get_y: '🆕',
};

export default function CartSummaryContent({
  appliedBenefits,
  cartItems,
  continueHref,
  couponClassName = 'coupon-inline',
  couponMessages,
  currency,
  currentUser,
  discountAmount,
  hasDiscountAmount,
  itemsPrice,
  messages,
  totalItems,
  totalPrice,
}) {
  const { clearDiscount, hasAppliedCoupon } = useCart();
  const subtotalLabel = useMemo(
    () =>
      interpolateCartMessage(
        readCartMessage(
          messages,
          'cart',
          'subtotalCard.subtotal',
          'Subtotal ({{count}} items)',
        ),
        { count: totalItems },
      ),
    [messages, totalItems],
  );
  const baseTotal = Number(itemsPrice || 0);
  const grossTotal = Math.max(0, baseTotal + Number(discountAmount || 0));
  const percentSaved =
    grossTotal > 0
      ? Math.min(
          100,
          Math.round((Number(discountAmount || 0) / grossTotal) * 100),
        )
      : 0;
  const resolvedCouponMessages = couponMessages || messages?.coupons || {};

  return (
    <>
      {!hasAppliedCoupon ? (
        <div className="subtotal-coupon w-100">
          <div className="coupon-wrapper d-flex justify-content-center">
            <CouponCard
              compact
              className={couponClassName}
              messages={resolvedCouponMessages}
            />
          </div>
        </div>
      ) : null}

      <h2 className="subtotal-title mb-0 fs-4 text-nowrap">{subtotalLabel}</h2>

      <div className="subtotal-amount fs-2">
        {hasDiscountAmount ? (
          <>
            <strong className="subtotal-price price--discounted">
              {formatCurrency(totalPrice, currency)}
            </strong>
            <del className="original-price price--original">
              {formatCurrency(itemsPrice, currency)}
            </del>
          </>
        ) : (
          <strong className="subtotal-price price--discounted">
            {formatCurrency(totalPrice, currency)}
          </strong>
        )}
      </div>

      {hasDiscountAmount ? (
        <div className="savings-progress mb-2" aria-hidden>
          <div
            className="savings-saved"
            style={{ width: `${percentSaved}%` }}
          />
          <div className="savings-label">{percentSaved}%</div>
        </div>
      ) : null}

      {appliedBenefits?.length ? (
        <div
          className="benefit-chips d-flex flex-wrap gap-2"
          aria-label={readCartMessage(
            messages,
            'cart',
            'subtotalCard.appliedBenefits',
            'Applied benefits',
          )}
        >
          {appliedBenefits.map((benefit, index) => (
            <span
              key={`${benefit.type || 'benefit'}-${index}`}
              className="benefit-chip rounded-pill px-2 py-1"
            >
              <span className="benefit-chip__icon me-1" aria-hidden>
                {benefitIcons[benefit.type] || '🆕'}
              </span>
              <span className="small">
                {getBenefitLabel(benefit, messages)}
              </span>
            </span>
          ))}
        </div>
      ) : null}

      {hasDiscountAmount ? (
        <SavedAmount
          id="cart-saved-amount"
          savedAmount={discountAmount}
          totalBase={baseTotal}
          currency={currency}
          label={readCartMessage(
            messages,
            'cart',
            'subtotalCard.youSaveLabel',
            'You save',
          )}
          showPercentage={false}
        />
      ) : null}

      {hasAppliedCoupon ? (
        <Button
          variant="link"
          className="subtotal-remove-coupon custom-btn-link flush text-danger"
          onClick={() => clearDiscount()}
          aria-label={readCartMessage(
            { coupons: resolvedCouponMessages },
            'coupons',
            'removeCoupon',
            'Remove coupon',
          )}
        >
          <FaTimes size={20} />
          <span className="fs-7">
            {readCartMessage(
              { coupons: resolvedCouponMessages },
              'coupons',
              'removeCoupon',
              'Remove coupon',
            )}
          </span>
        </Button>
      ) : null}

      <div className="subtotal-actions">
        <NoGutterRow className="align-items-center g-2">
          <Col xs="auto" className="bell-col">
            <BuyBoxBell
              currentUser={currentUser}
              messages={messages?.cart || {}}
              items={cartItems}
              copy={readCartMessage(
                messages,
                'cart',
                'callBack.cartCopy',
                'Leave your phone number and we will contact you about the items in your cart.',
              )}
              requiredMessage={readCartMessage(
                messages,
                'cart',
                'callBack.required',
                'Please provide your name and phone number.',
              )}
              successMessage={readCartMessage(
                messages,
                'cart',
                'callBack.success',
                'Callback request sent.',
              )}
              errorMessage={readCartMessage(
                messages,
                'cart',
                'callBack.error',
                'Unable to send callback request.',
              )}
              offcanvasId="cart-callback-offcanvas"
            />
          </Col>
          <Col>
            <div className="d-grid gap-2">
              <Link
                href={continueHref}
                className="app-button app-button--primary px-2"
              >
                {currentUser
                  ? readCartMessage(
                      messages,
                      'cart',
                      'subtotalCard.continueToShipping',
                      'Continue to Shipping',
                    )
                  : readCartMessage(
                      messages,
                      'cart',
                      'subtotalCard.loginToCheckout',
                      'Log In to Checkout',
                    )}
              </Link>
            </div>
          </Col>
        </NoGutterRow>
        <p className="cta-helper mt-2 mb-0">
          {readCartMessage(
            messages,
            'cart',
            'subtotalCard.reviewBeforePaying',
            'You can review your order before paying.',
          )}
        </p>
      </div>
    </>
  );
}
